import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import classNames from 'classnames'
import { isNil } from 'lodash'
import { IconBrandSlack } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'
import {
  getSlackOAuthUrl,
  fetchSlackStatus,
  disconnectSlack,
} from '@/reducers/organizations/organization.actions'

/**
 * Modal content for self-serve Slack notification setup (org admins). Shows a
 * Connect prompt when the org has no active Slack credential, or a connected
 * status with Change workspace + Disconnect when it does. The org-wide bot token is
 * installed once via OAuth; managers then toggle Slack delivery in their own
 * notification preferences.
 *
 * @param {object} props
 * @param {number} props.organizationId - org to manage; the backend re-checks
 *   the caller is authorized for it (authorize_for_org).
 * @param {() => void} props.onClose - closes the modal (injected by ButtonAndFormModal)
 */
export const SlackNotificationsForm = ({ organizationId, onClose }) => {
  const dispatch = useDispatch()
  // null while the initial status check is in flight.
  const [status, setStatus] = useState(null)
  const [busy, setBusy] = useState(false)

  const loadStatus = async () => {
    const result = await dispatch(fetchSlackStatus({ organizationId }))
    setStatus(result || { connected: false })
  }

  useEffect(() => {
    loadStatus()
  }, [organizationId, dispatch])

  const handleConnect = async () => {
    setBusy(true)
    // Full-page redirect to Slack; the OAuth callback returns the browser to
    // this page with ?slack=connected|error. Only safe relative paths honored.
    const url = await dispatch(
      getSlackOAuthUrl({ organizationId, returnTo: window.location.pathname })
    )
    if (url) {
      window.location.assign(url)
      return
    }
    setBusy(false)
  }

  const handleDisconnect = async () => {
    setBusy(true)
    const disconnected = await dispatch(disconnectSlack({ organizationId }))
    if (disconnected) {
      setStatus({ connected: false })
    }
    setBusy(false)
  }

  const loading = isNil(status)
  const connected = !loading && status.connected

  return (
    <div className="slack-notifications-form">
      {loading && <p className="small-text">Checking Slack connection…</p>}

      {connected && (
        <div className="flex-align-center small-gap" data-testid="slack-connected-status">
          <Pill small success>
            Slack connected
          </Pill>
          {status.last_updated && (
            <span className="small-text">
              Connected{' '}
              {new Date(status.last_updated).toLocaleDateString('en-US', { timeZone: 'UTC' })}
            </span>
          )}
        </div>
      )}

      {connected && (
        <p className="mt">
          Balto DMs your managers directly. You can move Balto to a different Slack workspace, or
          disconnect to stop Slack delivery for this organization.
        </p>
      )}

      {!loading && !connected && (
        <p>
          Connect your Slack workspace so Balto can DM managers their notifications. You’ll approve
          the Balto app in Slack and be brought right back here.
        </p>
      )}

      <div className="form-actions is-modal">
        {connected && (
          <Button
            type="button"
            critical
            onClick={handleDisconnect}
            disabled={busy}
            data-testid="disconnect-slack-button"
          >
            Disconnect
          </Button>
        )}
        {/* ml-auto on Cancel pushes it right, leaving Disconnect at the far left */}
        <Button type="button" className={classNames({ 'ml-auto': connected })} onClick={onClose}>
          Cancel
        </Button>
        {!loading && (
          <Button
            type="button"
            brand
            onClick={handleConnect}
            disabled={busy}
            data-testid="connect-slack-button"
          >
            <IconBrandSlack />
            {connected ? 'Change workspace' : 'Connect Slack'}
          </Button>
        )}
      </div>
    </div>
  )
}
